import { useNavigate } from '@tanstack/react-router';
import type { PatientSearchResult } from '@wodi/shared';
import { CommandPalette, SearchPicker, SearchTrigger, useShortcut } from '@wodi/ui';
import { useEffect, useState, type ReactNode, type Ref } from 'react';

import { PatientResultRow, resultText } from './patient-result-row.tsx';
import { isExactPatientMatch, usePatientSearch } from './use-patient-search.ts';

export const EMPTY_SEARCH_MESSAGE =
  'No patients match. Check the spelling or search by phone or ID number.';
export const SEARCH_PROMPT = 'Search by name, phone, ID number or patient number.';

const OPEN_EVENT = 'wodi:open-patient-search';

/** Opens the global patient search, as Ctrl/⌘K does (for buttons elsewhere, e.g. Home). */
export function openPatientSearch(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function countMessage(count: number): string {
  return `${String(count)} ${count === 1 ? 'patient' : 'patients'} found`;
}

/**
 * The patient search picker shared by the palette and Reception. The term stays in this
 * component's state; choosing a result hands back the patient, never the term.
 */
export function PatientSearchPicker({
  onSelect,
  autoFocus = false,
  emptyAction,
  inputValue,
  onInputChange,
  inputRef,
}: {
  inputRef?: Ref<HTMLInputElement>;
  onSelect: (result: PatientSearchResult) => void;
  autoFocus?: boolean;
  /** Shown with the empty state, e.g. "Register new patient". Receives the search text. */
  emptyAction?: (text: string) => ReactNode;
  inputValue: string;
  onInputChange: (value: string) => void;
}) {
  const search = usePatientSearch(inputValue);
  return (
    <SearchPicker<PatientSearchResult>
      label="Search patients"
      autoFocus={autoFocus}
      {...(inputRef === undefined ? {} : { inputRef })}
      promptMessage={SEARCH_PROMPT}
      inputValue={inputValue}
      onInputChange={onInputChange}
      status={search.status}
      items={search.items}
      itemsQuery={search.itemsQuery}
      getKey={(r) => r.id}
      getTextValue={resultText}
      renderItem={(r) => <PatientResultRow result={r} />}
      isExactMatch={isExactPatientMatch}
      onSelect={onSelect}
      emptyMessage={EMPTY_SEARCH_MESSAGE}
      {...(emptyAction === undefined ? {} : { emptyAction: emptyAction(inputValue) })}
      {...(search.errorMessage === undefined ? {} : { errorMessage: search.errorMessage })}
      onRetry={search.retry}
      countMessage={countMessage}
    />
  );
}

/**
 * Global patient search (plan §6.2): Ctrl/⌘K from anywhere, or the button in the top
 * bar. Enter on a result opens the patient page.
 */
export function PatientSearchPalette({
  registerAction,
}: {
  /** "Register new patient", for people who may register; gets the text to pre-fill. */
  registerAction?: (text: string, close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const navigate = useNavigate();

  const openPalette = () => {
    setText('');
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setText('');
  };
  useShortcut('mod+k', openPalette, { allowInFields: true, description: 'Search patients' });
  useEffect(() => {
    const onOpen = () => {
      setText('');
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  return (
    <>
      <SearchTrigger
        label="Search patients"
        shortLabel="Search"
        shortcut="mod+k"
        onPress={openPalette}
      />
      <CommandPalette
        isOpen={open}
        onOpenChange={(next) => {
          if (next) setOpen(true);
          else close();
        }}
        title="Patient search"
        hint="Arrow keys move through the results. Enter opens the patient. Esc closes."
      >
        <PatientSearchPicker
          autoFocus
          inputValue={text}
          onInputChange={setText}
          onSelect={(result) => {
            close();
            void navigate({ to: '/patients/$patientId', params: { patientId: result.id } });
          }}
          {...(registerAction === undefined
            ? {}
            : { emptyAction: (value: string) => registerAction(value, close) })}
        />
      </CommandPalette>
    </>
  );
}
